import {
  ExternalServiceConfiguration,
  ExternalServiceType,
  Integration,
  MerchantCashAdvance,
  MerchantCashAdvanceStatus,
  Rights,
  useOfferByMcaIdQuery,
  useProfileQuery,
  UserPreferences
} from '../../graphql/types';
import Tooltip from '@mui/material/Tooltip';

import {
  Box,
  Chip,
  Collapse,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import {
  CalendarOutlined,
  CarryOutOutlined,
  ClockCircleOutlined,
  DollarCircleOutlined,
  DollarOutlined,
  RocketOutlined,
  CloseOutlined,
  PercentageOutlined,
  RiseOutlined,
  SyncOutlined,
  BankOutlined
} from '@ant-design/icons';
import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import McaPerformanceChart from './McaPerformanceChart';
import MainCard from '../../components/MainCard';
import useAuth from '../../hooks/useAuth';
import { calculateSyndicationPnlNet } from './SyndicationDetails';
import { capitalizeFirstLetter } from '../../utils/strings';
import { isUserFunderSyndicator } from '../../utils/mca';
import AssetLogo from 'sections/AssetLogo';
import PlatformCompanyLogo from '../../utils/platformCompanyLogo';
import McaCommissionsTooltip from './McaCommisionsTooltip';
import { debounce } from 'lodash';
import UserListItem from '../../components/UserListItem';
import { useTheme } from '@mui/material/styles';
import { NumericFormat } from 'react-number-format';
import MerchantProfileList from './MerchantProfileList';
import UserProfileList from './UserProfileList';
import McaUnderwriterWidget from './McaUnderwriterWidget';
import { latestExternalConnectionByType } from 'utils/latestExternalConnectionByType';
import { useExternalServiceLogo } from 'utils/externalServiceLogo';
import Avatar from 'components/@extended/Avatar';
import CustomTooltip from 'components/CustomTooltip';
import { ApplicationStatusChip } from '../offers/ApplicationStatusChip';

interface Props {
  mca: MerchantCashAdvance;
  onHeightChange?: (height: number) => void;
  investorId: string | undefined;
  rowExpanded?: boolean;
}

const McaDetails = ({ mca, investorId, rowExpanded = false, onHeightChange = () => {} }: Props) => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const { data: offerByMcaData } = useOfferByMcaIdQuery({
    variables: { mcaId: mca.id },
    fetchPolicy: 'network-only',
    skip: !isSuperAdmin
  });
  const linkedApplication = offerByMcaData?.offerByMcaId?.application;
  const { data } = useProfileQuery({ fetchPolicy: 'cache-first' });
  const preferences = useMemo(() => data?.profile.preferences as UserPreferences, [data]);
  const syndication = mca.syndications?.find((s) => s.userId === (investorId || currentUser?.id));
  const pendingBalance = mca.balancePnd || 0;
  const clearedBalance = mca.balanceclr || 0;
  const clearedBalanceWithFees = mca.balanceClrWFees || 0;
  const pendingBalanceWithFees = mca.balancePndWFees || 0;
  const theme = useTheme();
  const pnlAmountNet =
    (!!syndication && !currentUser?.rights.includes(Rights.ViewMcaPnl) ? calculateSyndicationPnlNet(mca, syndication) : mca.pnlNet) || 0;
  const pnlAmountGross =
    (!!syndication && !currentUser?.rights.includes(Rights.ViewMcaPnl) ? calculateSyndicationPnlNet(mca, syndication) : mca.pnlGross) || 0;
  const canSeeMcaFees = currentUser?.rights.includes(Rights.ViewMcaFees);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isHoveredPaid, setIsHoveredPaid] = React.useState(false);
  const lastIntegration = mca.integration;
  const [hoveredFunding, setHoveredFunding] = useState(false);
  const [hoveredPayback, setHoveredPayback] = useState(false);
  const [hoveredPrimaryFunder, setHoveredPrimaryFunder] = useState(false);
  const [hoveredMerchant, setHoveredMerchant] = useState(false);
  const [hoveredBroker, setHoveredBroker] = useState(false);
  const [hoveredManager, setHoveredManager] = useState(false);
  const [hoveredLegal, setHoveredLegal] = useState(false);
  const IsFunderSyndicator = useMemo(() => {
    return isUserFunderSyndicator(mca?.syndications, currentUser?.id);
  }, [mca?.syndications, currentUser?.id]);
  const hasViewMcaFunderRights = currentUser?.rights.includes(Rights.ViewMcaFunder) || mca?.currentUserRights.includes(Rights.ViewMcaFunder);

  const debouncedSetHoveredPrimaryFunder = useCallback(
    debounce((hovered) => {
      setHoveredPrimaryFunder(hovered);
    }, 100),
    []
  );
  useEffect(() => () => debouncedSetHoveredPrimaryFunder.cancel(), [debouncedSetHoveredPrimaryFunder]);

  const debouncedSetHoveredFunding = useCallback(
    debounce((hovered) => {
      setHoveredFunding(hovered);
    }, 100),
    []
  );
  useEffect(() => () => debouncedSetHoveredFunding.cancel(), [debouncedSetHoveredFunding]);

  const debouncedSetHoveredPayback = useCallback(
    debounce((hovered) => {
      setHoveredPayback(hovered);
    }, 100),
    []
  );
  useEffect(() => () => debouncedSetHoveredPayback.cancel(), [debouncedSetHoveredPayback]);

  const debouncedSetHoveredMerchant = useCallback(
    debounce((hovered) => {
      setHoveredMerchant(hovered);
    }, 100),
    []
  );
  useEffect(() => () => debouncedSetHoveredMerchant.cancel(), [debouncedSetHoveredMerchant]);

  const debouncedSetHoveredBroker = useCallback(
    debounce((hovered) => {
      setHoveredBroker(hovered);
    }, 100),
    []
  );
  useEffect(() => () => debouncedSetHoveredBroker.cancel(), [debouncedSetHoveredBroker]);

  const debouncedSetHoveredManager = useCallback(
    debounce((hovered) => {
      setHoveredManager(hovered);
    }, 100),
    []
  );
  useEffect(() => () => debouncedSetHoveredManager.cancel(), [debouncedSetHoveredManager]);

  const debouncedSetHoveredLegal = useCallback(
    debounce((hovered) => {
      setHoveredLegal(hovered);
    }, 100),
    []
  );
  useEffect(() => () => debouncedSetHoveredLegal.cancel(), [debouncedSetHoveredLegal]);

  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const calculateGridHeight = () => {
      if (gridRef.current) {
        const containerHeight = gridRef.current.getBoundingClientRect().height;
        onHeightChange(containerHeight);
      }
    };

    calculateGridHeight();
    window.addEventListener('resize', calculateGridHeight);
    return () => window.removeEventListener('resize', calculateGridHeight);
  }, [onHeightChange]);

  const simplicityServiceConfig = useMemo(() => {
    if (!mca) return null;
    const connection = latestExternalConnectionByType(mca, [ExternalServiceType.SimplicityCollect]);
    const serviceConfig = connection?.externalServiceConfiguration;
    if (serviceConfig?.type === ExternalServiceType.SimplicityCollect && mca.status === MerchantCashAdvanceStatus.Collections) {
      return serviceConfig as ExternalServiceConfiguration;
    }
    return null;
  }, [mca]);

  const simplicityLogo = useExternalServiceLogo(simplicityServiceConfig);

  const simplicityConfig = useMemo(() => {
    if (!simplicityServiceConfig) return null;
    return {
      logo: simplicityLogo,
      name: simplicityServiceConfig.name || 'Simplicity Collect'
    };
  }, [simplicityServiceConfig, simplicityLogo]);

  const mcaCompletion = mca.completion || 0;
  const paidChipColor =
    mcaCompletion < 25
      ? 'salmon'
      : mcaCompletion >= 25 && mcaCompletion < 50
        ? 'sandybrown'
        : mcaCompletion >= 50 && mcaCompletion < 75
          ? 'dodgerblue'
          : 'springgreen';
  return (
    <Box p={2}>
      <Grid ref={gridRef} container rowSpacing={0} columnSpacing={1} pr={1}>
        <Grid item xs={12} sm={6} md={4} lg={2.1} xl={2.5} mt={1}>
          <MainCard
              border={true}
              shadow={3}
              boxShadow
              sx={{
                height: 450,
                minWidth: 210,
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
                '&::-webkit-scrollbar': {
                  width: '5px !important'
                },
                '&::-webkit-scrollbar-track': {
                  background: '#2a2d3c !important'
                }
              }}
          >
            {mca.fundingDate && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ position: 'absolute', top: 12, left: 12 }}
              >
                <RocketOutlined style={{ color: theme.palette.turquoise.main, fontSize: '10px' }} />
                <Typography sx={{ fontSize: '10px !important' }}>{getFormattedDateUTC(mca.fundingDate)}</Typography>
              </Stack>
            )}
            <Grid item xs={12}>
              <Stack spacing={0.5} alignItems="left">
                <Stack spacing={0.5} alignItems="center">
                  <Typography color="#706CE6">MERCHANT</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} className={!rowExpanded ? 'mca-details-content' : ''}>
              <MerchantProfileList
                merchant={mca.merchant}
                debouncedSetHoveredMerchant={debouncedSetHoveredMerchant}
                isHovered={hoveredMerchant}
                isRowExpanded={rowExpanded}
                isCollapsed={true}
                disableContactInfoClamp={true}
              />
            </Grid>
            <Divider sx={{ backgroundColor: '#4C4C4E' }} />
            {(currentUser?.rights.includes(Rights.ViewMcaFundingCompany) || hasViewMcaFunderRights ||
              currentUser?.rights.includes(Rights.ViewMcaBroker) ||
              currentUser?.rights.includes(Rights.ViewMcaIntegration)) && (
              <>
                <Grid item xs={12}>
                  <Stack spacing={0.5} alignItems="left">
                    <Stack spacing={0.5} alignItems="center">
                      <Typography color="#706CE6" sx={{ paddingTop: 1.5, paddingBottom: 1 }}>
                        GENERAL
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} className={!rowExpanded ? 'mca-details-content' : ''}>
                  <List component="nav" aria-label="Merchant profile" sx={{ py: 1, '& .MuiListItem-root': { p: 0 } }}>
                    {currentUser?.rights.includes(Rights.ViewMcaIntegration) && lastIntegration && (
                      <>
                        <ListItem>
                          <Stack direction="row" spacing={1} alignItems="center" width="100% " sx={{ flex: 1, mb: 1 }}>
                            <PlatformCompanyLogo integration={lastIntegration} size="xs" />
                            <Stack spacing={0.2} sx={{ flex: 1, minWidth: 0 }}>
                              <CustomTooltip title={lastIntegration.name}>
                                <Typography
                                  noWrap
                                  sx={{
                                    '@media (min-width: 1280px) and (max-width: 1700px)': {
                                      maxWidth: '170px'
                                    }
                                  }}
                                >
                                  {lastIntegration.name}
                                </Typography>
                              </CustomTooltip>
                            </Stack>

                            {linkedApplication && (
                              <Stack sx={{ ml: 'auto' }}>
                                <ApplicationStatusChip
                                  id={linkedApplication.id}
                                  label={String(linkedApplication.number)}
                                  color={linkedApplication.status?.color || ''}
                                  description={linkedApplication.status?.description || ''}
                                  navigateToDetails={true}
                                  detailsPath="/applications"
                                  mca={true}
                                  navigationState={rowExpanded ? { fromCashAdvances: true } : { fromMca: mca.id }}
                                />
                              </Stack>
                            )}

                            {currentUser?.rights.includes(Rights.ViewMcaFundingCompany) && (
                              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ ml: 'auto' }}>
                                <Stack direction="row" spacing={-1.5}>
                                  <CustomTooltip title={mca.company?.legalName}>
                                    <span>
                                      <AssetLogo logo={mca.company?.logo} name={mca.company?.legalName} size="xs" />
                                    </span>
                                  </CustomTooltip>

                                  {simplicityConfig && (
                                    <CustomTooltip title={simplicityConfig.name}>
                                      <span>
                                        <Avatar src={simplicityConfig.logo || undefined} alt={simplicityConfig.name} size="xs" />
                                      </span>
                                    </CustomTooltip>
                                  )}
                                </Stack>
                              </Stack>
                            )}
                          </Stack>
                        </ListItem>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      </>
                    )}
                    {((mca?.funder && IsFunderSyndicator) || hasViewMcaFunderRights) && (
                      <ListItem
                        sx={{ pt: 1 }}
                        onMouseEnter={() => debouncedSetHoveredPrimaryFunder(true)}
                        onMouseLeave={() => debouncedSetHoveredPrimaryFunder(false)}
                      >
                        <McaUnderwriterWidget underwriter={mca?.funder} />
                      </ListItem>
                    )}
                    {((!!mca.secondaryFunders?.length && IsFunderSyndicator) ||
                      (!!mca.secondaryFunders?.length && hasViewMcaFunderRights)) && (
                      <>
                        <Collapse in={hoveredPrimaryFunder} timeout="auto" easing="cubic-bezier(0.4, 0, 0.2, 1)" unmountOnExit>
                          {mca.secondaryFunders?.map((secondaryFunder, index) => (
                            <ListItem key={secondaryFunder.id}>
                              <McaUnderwriterWidget underwriter={secondaryFunder} />
                            </ListItem>
                          ))}
                        </Collapse>
                      </>
                    )}
                    <Stack
                      spacing={1}
                      onMouseEnter={() => debouncedSetHoveredBroker(true)}
                      onMouseLeave={() => debouncedSetHoveredBroker(false)}
                    >
                      {mca?.broker?.name && currentUser?.rights.includes(Rights.ViewMcaBroker) && (
                        <UserListItem userData={mca?.broker} role="Broker" showCompany={true} />
                      )}
                      {(mca?.broker?.email || mca?.broker?.phone) && <UserProfileList user={mca?.broker} isHovered={hoveredBroker} />}
                    </Stack>
                    <Stack
                      spacing={1}
                      onMouseEnter={() => debouncedSetHoveredManager(true)}
                      onMouseLeave={() => debouncedSetHoveredManager(false)}
                    >
                      {mca?.manager?.name && currentUser?.rights.includes(Rights.ViewMcaIsoManager) && (
                        <UserListItem userData={mca?.manager} role="ISO Manager" />
                      )}
                      {(mca?.manager?.email || mca?.manager?.data?.bizPhone) && (
                        <UserProfileList user={mca?.manager} isHovered={hoveredManager} />
                      )}
                    </Stack>
                    <Stack
                      spacing={1}
                      onMouseEnter={() => debouncedSetHoveredLegal(true)}
                      onMouseLeave={() => debouncedSetHoveredLegal(false)}
                    >
                      {((mca?.legal?.name && IsFunderSyndicator) ||
                        (mca?.legal?.name && hasViewMcaFunderRights)) && (
                        <UserListItem userData={mca?.legal} role="Legal" showCompany={true} />
                      )}
                      {(mca?.legal?.email || mca?.legal?.phone) && <UserProfileList user={mca?.legal} isHovered={hoveredLegal} />}
                    </Stack>
                  </List>
                </Grid>
              </>
            )}
          </MainCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.5} xl={2.5} mt={1}>
          <MainCard
            border={true}
            shadow={3}
            mt={0.5}
            boxShadow
            sx={{ height: 450, minWidth: 230 }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Grid item xs={12}>
              <Stack spacing={2.5} alignItems="center">
                <Stack spacing={0.5} alignItems="center">
                  <Typography style={{ color: theme.palette.limegreen.main }}>MCA INFO</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} className={!rowExpanded ? 'mca-details-content' : ''}>
              <List component="nav" aria-label="Cash advance profile" sx={{ '& .MuiListItem-root': { p: 0 } }}>
                <ListItem onMouseEnter={() => debouncedSetHoveredFunding(true)} onMouseLeave={() => debouncedSetHoveredFunding(false)}>
                  <ListItemIcon>
                    <DollarOutlined style={{ color: theme.palette.limegreen.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Funding</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={mca.amount} />
                  </ListItemSecondaryAction>
                </ListItem>
                {currentUser?.rights.includes(Rights.ViewMcaNetWire) && (
                  <Collapse in={hoveredFunding} timeout={600} easing="cubic-bezier(0.4, 0, 0.2, 1)" unmountOnExit>
                    <ListItem>
                      <ListItemIcon>
                        <RocketOutlined style={{ color: theme.palette.lightskyblue.main }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">Net Wire</Typography>} />
                      <ListItemSecondaryAction>
                        <CurrencyText value={mca.netWire || 0} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Collapse>
                )}
                <ListItem onMouseEnter={() => debouncedSetHoveredPayback(true)} onMouseLeave={() => debouncedSetHoveredPayback(false)}>
                  <ListItemIcon>
                    <DollarOutlined style={{ color: theme.palette.limegreen.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Payback</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={mca.payback} />
                  </ListItemSecondaryAction>
                </ListItem>
                {currentUser?.rights.includes(Rights.ViewMcaBalanceWithFees) && (
                  <Collapse in={hoveredPayback} timeout={600} easing="cubic-bezier(0.4, 0, 0.2, 1)" unmountOnExit>
                    <ListItem>
                      <ListItemIcon>
                        <DollarOutlined style={{ color: theme.palette.orangered.main }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">Payback w/ Fees</Typography>} />
                      <ListItemSecondaryAction>
                        <CurrencyText value={mca?.paybackWFee} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Collapse>
                )}
                <ListItem>
                  <ListItemIcon>
                    <SyncOutlined style={{ color: theme.palette.limegreen.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Recurring Pmt</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={mca.payback / mca.term} />
                  </ListItemSecondaryAction>
                </ListItem>
                {currentUser?.rights.includes(Rights.ViewMcaCommission) && (
                  <ListItem>
                    <ListItemIcon>
                      <Tooltip
                        title={<McaCommissionsTooltip mca={mca} />}
                        componentsProps={{ tooltip: { sx: { backgroundColor: 'transparent' } } }}
                        placement="top-start"
                      >
                        <PercentageOutlined style={{ color: theme.palette.gold.main }} />
                      </Tooltip>
                    </ListItemIcon>
                    <ListItemText primary={<Typography color="secondary">Comm</Typography>} />
                    <ListItemSecondaryAction>
                      <div>
                        <NumericFormat value={mca.commissionPercentage} displayType="text" decimalScale={2} thousandSeparator suffix="%" />{' '}
                        (
                        <CurrencyText value={(mca.amount * (mca.commissionPercentage || 0)) / 100} />)
                      </div>
                    </ListItemSecondaryAction>
                  </ListItem>
                )}
                {!!syndication && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <PercentageOutlined style={{ color: theme.palette.gold.main }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">My Brk Comm</Typography>} />
                      <ListItemSecondaryAction>
                        <div>
                          <NumericFormat
                            value={syndication.brkCommissionPercentage}
                            displayType="text"
                            decimalScale={2}
                            thousandSeparator
                            suffix="%"
                          />{' '}
                          (
                          <CurrencyText value={(syndication.brkCommissionPercentage / 100) * syndication.amount} />)
                        </div>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PercentageOutlined style={{ color: theme.palette.gold.main }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">My ISO Comm</Typography>} />
                      <ListItemSecondaryAction>
                        <div>
                          <NumericFormat
                            value={syndication.isoCommissionPercentage}
                            displayType="text"
                            decimalScale={2}
                            thousandSeparator
                            suffix="%"
                          />{' '}
                          (
                          <CurrencyText value={(syndication.isoCommissionPercentage / 100) * syndication.amount} />)
                        </div>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </>
                )}
                <ListItem>
                  <ListItemIcon>
                    <RiseOutlined style={{ color: theme.palette.gold.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Factor</Typography>} />
                  <ListItemSecondaryAction>
                    <NumericFormat value={mca.factorRate} displayType="text" decimalScale={5} thousandSeparator />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ClockCircleOutlined style={{ color: theme.palette.limegreen.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Term</Typography>} />
                  <ListItemSecondaryAction>{mca.term}</ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CarryOutOutlined style={{ color: theme.palette.limegreen.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Start Date</Typography>} />
                  <ListItemSecondaryAction>{getFormattedDateUTC(mca.startDate)}</ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarOutlined style={{ color: theme.palette.limegreen.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Expected End</Typography>} />
                  <ListItemSecondaryAction>{getFormattedDateUTC(mca.endDate)}</ListItemSecondaryAction>
                </ListItem>
                <Collapse in={isHovered} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {canSeeMcaFees &&
                      mca?.fundingFee?.map((fee, i) => {
                        const FeeIcon = DollarCircleOutlined;
                        return (
                          <React.Fragment key={i}>
                            <ListItem style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <ListItemIcon style={{ position: 'absolute', top: 7, color: '#FF7A45' }}>
                                  <FeeIcon />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography
                                      color="secondary"
                                      style={{
                                        whiteSpace: 'normal',
                                        overflowWrap: 'break-word',
                                        paddingLeft: '24px'
                                      }}
                                    >
                                      {capitalizeFirstLetter(fee.comment)}
                                    </Typography>
                                  }
                                />
                              </div>
                              <div style={{ alignSelf: 'flex-start', padding: '5px 15px 0 0' }}>
                                <CurrencyText value={fee.amount} />
                              </div>
                            </ListItem>
                          </React.Fragment>
                        );
                      })}
                    {syndication?.orgFeeCommissionPercentage > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <DollarOutlined style={{ color: theme.palette.lightsalmon.main }} />
                        </ListItemIcon>
                        <ListItemText primary={<Typography color="secondary">My Org Fee</Typography>} />
                        <ListItemSecondaryAction>
                          <CurrencyText value={(syndication.orgFeeCommissionPercentage / 100) * syndication.amount} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    )}
                    {syndication?.wireFee > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <DollarCircleOutlined style={{ color: '#FF7A45' }} />
                        </ListItemIcon>
                        <ListItemText primary={<Typography color="secondary">My Wire Fee</Typography>} />
                        <ListItemSecondaryAction>
                          <CurrencyText value={syndication?.wireFee} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    )}
                    {syndication?.crmFee > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <DollarOutlined style={{ color: theme.palette.lightsalmon.main }} />
                        </ListItemIcon>
                        <ListItemText primary={<Typography color="secondary">My Crm Fee</Typography>} />
                        <ListItemSecondaryAction>
                          <CurrencyText value={syndication?.crmFee} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    )}
                    {syndication?.otherFee > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <DollarOutlined style={{ color: theme.palette.lightsalmon.main }} />
                        </ListItemIcon>
                        <ListItemText primary={<Typography color="secondary">My Other Fee</Typography>} />
                        <ListItemSecondaryAction>
                          <CurrencyText value={syndication?.otherFee} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    )}
                  </List>
                </Collapse>
              </List>
            </Grid>
          </MainCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.5} xl={2.5} mt={1}>
          <MainCard border={false} shadow={3} mt={0.5} boxShadow sx={{ height: 450, maxHeight: 450, minWidth: 230, overflowY: 'auto' }}>
            <Grid item xs={12}>
              <Stack spacing={2.5} alignItems="center">
                <Stack spacing={0.5} alignItems="center">
                  <Typography style={{ color: theme.palette.lightskyblue.main }}>MCA PERFORMANCE</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} className={!rowExpanded ? 'mca-details-content' : ''}>
              <List component="nav" aria-label="Performance profile" sx={{ '& .MuiListItem-root': { p: 0 } }}>
                <ListItem onMouseEnter={() => setIsHoveredPaid(true)} onMouseLeave={() => setIsHoveredPaid(false)}>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: theme.palette.lightskyblue.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Paid</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={mca.amtpaidclr || 0} />
                  </ListItemSecondaryAction>
                </ListItem>
                {currentUser?.rights.includes(Rights.ViewMcaBalanceWithFees) && (
                  <Collapse in={isHoveredPaid} timeout="auto" unmountOnExit>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: theme.palette.orangered.main }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">Paid w/ Fees</Typography>} />
                      <ListItemSecondaryAction>
                        <CurrencyText value={mca?.amtpaidclr + mca?.totalfines || 0} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Collapse>
                )}
                <ListItem>
                  <ListItemIcon>
                    <PercentageOutlined style={{ color: theme.palette.lightskyblue.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Paid</Typography>} />
                  <ListItemSecondaryAction>
                    <NumericFormat value={mca.completion} displayType="text" decimalScale={2} thousandSeparator suffix="%" />
                  </ListItemSecondaryAction>
                </ListItem>
                {currentUser?.rights.includes(Rights.ViewMcaPnl) && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: theme.palette.lightskyblue.main }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">Net P&L</Typography>} />
                      <ListItemSecondaryAction>
                        <Chip
                          color={pnlAmountNet > 0 ? 'success' : 'error'}
                          label={<CurrencyText value={pnlAmountNet} />}
                          size="small"
                          variant="light"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: theme.palette.lightskyblue.main }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">Gross P&L</Typography>} />
                      <ListItemSecondaryAction>
                        <Chip
                          color={pnlAmountGross > 0 ? 'success' : 'error'}
                          label={<CurrencyText value={pnlAmountGross} />}
                          size="small"
                          variant="light"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </>
                )}
                <ListItem>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: theme.palette.lightskyblue.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Cleared Bal.</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={clearedBalance} />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DollarCircleOutlined style={{ color: theme.palette.lightskyblue.main }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">Pending Bal.</Typography>} />
                  <ListItemSecondaryAction>
                    <CurrencyText value={pendingBalance} />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CloseOutlined
                      style={{
                        color: (mca.nrt || 0) > 0 ? theme.palette.lightcoral.main : theme.palette.lightskyblue.main
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={<Typography color="secondary">NSF Counter</Typography>} />
                  <ListItemSecondaryAction>{mca.nrt || '-'}</ListItemSecondaryAction>
                </ListItem>
                {currentUser?.rights.includes(Rights.ViewMcaBalanceWithFees) && (
                  <>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: theme.palette.orangered.main }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography color="secondary">
                            Cleared Bal.
                            <br />
                            w/ Unpaid Fees
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <CurrencyText value={clearedBalanceWithFees} />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: theme.palette.orangered.main }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography color="secondary">
                            Pending Bal.
                            <br />
                            w/ Unpaid Fees
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <CurrencyText value={pendingBalanceWithFees} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </>
                )}
              </List>
            </Grid>
            {!!syndication && (
              <>
                <Grid item xs={12}>
                  <Stack spacing={2.5} alignItems="center">
                    <Stack spacing={0.5} alignItems="center">
                      <Typography style={{ color: theme.palette.lightskyblue.main }}>MY MCA PERFORMANCE</Typography>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <List component="nav" aria-label="Performance profile" sx={{ '& .MuiListItem-root': { p: 0 } }}>
                    <ListItem>
                      <ListItemIcon>
                        <PercentageOutlined style={{ color: '#13C2C2' }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">My Participation</Typography>} />
                      <ListItemSecondaryAction>
                        <NumericFormat
                          value={syndication.syndicationPercentage}
                          displayType="text"
                          decimalScale={2}
                          thousandSeparator
                          suffix="%"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: '#13C2C2' }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">My Paid</Typography>} />
                      <ListItemSecondaryAction>
                        <CurrencyText value={(mca.amtpaidclr || 0) * (syndication.syndicationPercentage / 100)} />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: '#13C2C2' }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">My Cleared Bal.</Typography>} />
                      <ListItemSecondaryAction>
                        <CurrencyText value={clearedBalance * (syndication.syndicationPercentage / 100)} />
                      </ListItemSecondaryAction>
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DollarCircleOutlined style={{ color: '#13C2C2' }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography color="secondary">My Pending Bal.</Typography>} />
                      <ListItemSecondaryAction>
                        <CurrencyText value={pendingBalance * (syndication.syndicationPercentage / 100)} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </Grid>
              </>
            )}
          </MainCard>
        </Grid>
        {!preferences?.hidePerformanceChart && (
          <Grid item xs={12} sm={12} md={8} lg={4.9} xl={4.5} mt={1}>
            <MainCard border={false} shadow={3} mt={0.5} boxShadow sx={{ height: 450, minWidth: 250 }}>
              <Grid item xs={12}>
                <Stack spacing={0.5} alignItems="center">
                  <Typography sx={{ color: 'goldenrod' }}>PERFORMANCE CHART</Typography>
                </Stack>
              </Grid>
              <Divider />
              <Grid item xs={12}>
                <Box sx={{ position: 'relative' }}>
                  <Chip
                    label={
                      <span style={{ fontSize: '12px', color: paidChipColor }}>
                        {'PAID:'} <NumericFormat value={Math.round(mcaCompletion)} displayType="text" thousandSeparator suffix="%" />
                      </span>
                    }
                    sx={{
                      position: 'absolute',
                      right: 1,
                      zIndex: 10,
                      backgroundColor: '#4A4B52',
                      height: '40px',
                      border: `1px solid ${paidChipColor}`
                    }}
                    size="medium"
                  />
                  <McaPerformanceChart mca={mca} />
                </Box>
              </Grid>
            </MainCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default McaDetails;
